import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Camera, MapPin, PencilLine, Save, Search, Star, Upload, UserRound, UsersRound, X } from "lucide-react";
import CropControls from "../components/CropControls";
import DeleteConfirmation from "../components/DeleteConfirmation";
import MemberGate from "../components/MemberGate";
import PhotoStage from "../components/PhotoStage";
import { ErrorMessage, Loading } from "../components/Loading";
import { api } from "../lib/api";
import { cropStyle, normalizeCrop } from "../lib/crop";
import { useApi } from "../hooks/useApi";

function orderAlbumPhotos(photos, photoIds) {
  if (!Array.isArray(photoIds) || !photoIds.length) return photos;
  const ranks = new Map(photoIds.map((photoId, index) => [photoId, index]));
  return photos
    .map((photo, index) => ({ photo, index, rank: ranks.get(photo.id) }))
    .sort((left, right) => {
      if (left.rank !== undefined && right.rank !== undefined) return left.rank - right.rank;
      if (left.rank !== undefined) return -1;
      if (right.rank !== undefined) return 1;
      return left.index - right.index;
    })
    .map(({ photo }) => photo);
}

function EditReunion({ reunion, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: reunion.title || "",
    date: reunion.date || "",
    location: reunion.location || "",
    organizer: reunion.organizer || "",
    contact: reunion.contact || "",
    description: reunion.description || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const dialog = useRef(null);

  useEffect(() => dialog.current?.focus(), []);

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const result = await api(`/api/reunions/${encodeURIComponent(reunion.id)}`, {
        method: "PATCH",
        body: JSON.stringify(form),
      });
      onSaved(result.reunion);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal-panel reunion-editor" role="dialog" aria-modal="true" aria-labelledby="edit-reunion-title" tabIndex="-1" ref={dialog}>
        <button className="icon-button modal-close" type="button" onClick={onClose} aria-label="Close reunion editor"><X aria-hidden="true" /></button>
        <p className="kicker">Your reunion page</p>
        <h2 id="edit-reunion-title">Keep the chapter current.</h2>
        <p className="modal-intro">Update the gathering details whenever plans change. The reunion address and shared photographs stay in place.</p>
        <form className="form-grid" onSubmit={submit}>
          <label className="form-field form-field--wide">Reunion title<input required maxLength="100" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} /></label>
          <label className="form-field">Date<input required type="date" value={form.date} onChange={(event) => setForm({ ...form, date: event.target.value })} /></label>
          <label className="form-field">Place<input required maxLength="160" value={form.location} onChange={(event) => setForm({ ...form, location: event.target.value })} /></label>
          <label className="form-field">Organizer<input maxLength="100" value={form.organizer} onChange={(event) => setForm({ ...form, organizer: event.target.value })} /></label>
          <label className="form-field">Contact<input maxLength="180" value={form.contact} onChange={(event) => setForm({ ...form, contact: event.target.value })} placeholder="Email or WhatsApp" /></label>
          <label className="form-field form-field--wide">What should classmates know?<textarea required maxLength="1200" rows="6" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></label>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <div className="reunion-editor__actions">
            <button className="button button--ghost" type="button" onClick={onClose}>Cancel</button>
            <button className="button button--coral" disabled={saving} type="submit"><Save aria-hidden="true" /> {saving ? "Saving…" : "Save reunion"}</button>
          </div>
        </form>
      </section>
    </div>
  );
}

function EditReunionPhoto({ reunion, albumId, photo, onClose, onSaved }) {
  const sharedAlbum = albumId === `${reunion.id}-shared`;
  const isCover = reunion.cover === photo.src;
  const [caption, setCaption] = useState(photo.alt || "");
  const [makeCover, setMakeCover] = useState(isCover);
  const [crop, setCrop] = useState(() => normalizeCrop(photo.crop));
  const [commentsDisabled, setCommentsDisabled] = useState(Boolean(photo.commentsDisabled));
  const [collegeDaysDisabled, setCollegeDaysDisabled] = useState(Boolean(photo.collegeDaysDisabled));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const dialog = useRef(null);

  useEffect(() => dialog.current?.focus(), []);

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const endpoint = sharedAlbum
        ? `/api/reunions/${encodeURIComponent(reunion.id)}/photos/${encodeURIComponent(photo.id)}`
        : `/api/admin/reunion-albums/${encodeURIComponent(albumId)}/photos/${encodeURIComponent(photo.id)}`;
      const result = await api(endpoint, {
        method: "PATCH",
        body: JSON.stringify({ caption, makeCover, crop, commentsDisabled, collegeDaysDisabled }),
      });
      await onSaved(result);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal-panel reunion-photo-editor" role="dialog" aria-modal="true" aria-labelledby="edit-reunion-photo-title" tabIndex="-1" ref={dialog}>
        <button className="icon-button modal-close" type="button" onClick={onClose} aria-label="Close photograph editor"><X aria-hidden="true" /></button>
        <div className="reunion-photo-editor__preview"><img src={photo.src} alt="" style={cropStyle(crop)} /></div>
        <div>
          <p className="kicker">Photograph details</p>
          <h2 id="edit-reunion-photo-title">Give this frame context.</h2>
          <form className="reunion-photo-editor__form" onSubmit={submit}>
            <label className="form-field">
              Caption or picture comment
              <textarea required maxLength="240" rows="4" value={caption} onChange={(event) => setCaption(event.target.value)} />
              <small>When possible, include the names of the people in the photograph.</small>
            </label>
            <CropControls crop={crop} onChange={setCrop} compact />
            <div className="reunion-photo-options">
              {sharedAlbum && (
                <label><input type="checkbox" checked={commentsDisabled} onChange={(event) => setCommentsDisabled(event.target.checked)} /><span><b>Close comments</b><small>Existing comments remain visible, but no new comments can be posted.</small></span></label>
              )}
              <label><input type="checkbox" checked={collegeDaysDisabled} onChange={(event) => setCollegeDaysDisabled(event.target.checked)} /><span><b>Keep out of College Days</b><small>The super administrator’s move action will be unavailable for this photograph.</small></span></label>
            </div>
            {sharedAlbum && (
              <label className={`cover-choice${isCover ? " is-current" : ""}`}>
                <input type="checkbox" checked={makeCover} disabled={isCover} onChange={(event) => setMakeCover(event.target.checked)} />
                <Star aria-hidden="true" />
                <span><b>{isCover ? "Current reunion cover" : "Use as reunion cover"}</b><small>The cover appears on the reunion timeline and at the top of this page.</small></span>
              </label>
            )}
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <div className="reunion-editor__actions">
              <button className="button button--ghost" type="button" onClick={onClose}>Cancel</button>
              <button className="button button--coral" disabled={saving} type="submit"><Save aria-hidden="true" /> {saving ? "Saving…" : "Save photograph"}</button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

function ManageReunionEditors({ reunion, onClose, onSaved }) {
  const loader = useCallback(() => api("/api/admin/accounts"), []);
  const { data, error: loadError, loading } = useApi(loader, [loader]);
  const [query, setQuery] = useState("");
  const [ownerMemberId, setOwnerMemberId] = useState(reunion.owner?.memberId || reunion.createdBy?.memberId || "");
  const [coAuthorMemberIds, setCoAuthorMemberIds] = useState(() => new Set((reunion.coAuthors || []).map((person) => person.memberId)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const accounts = data?.accounts || [];
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return accounts.filter((account) => !account.memorial && (!needle || [account.firstName, account.lastName, account.branch, account.status].join(" ").toLowerCase().includes(needle)));
  }, [accounts, query]);

  function chooseOwner(memberId) {
    setOwnerMemberId(memberId);
    setCoAuthorMemberIds((current) => {
      const next = new Set(current);
      next.delete(memberId);
      return next;
    });
  }

  function toggleCoAuthor(memberId) {
    setCoAuthorMemberIds((current) => {
      const next = new Set(current);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  }

  async function submit(event) {
    event.preventDefault();
    if (!ownerMemberId) return;
    setSaving(true);
    setError("");
    try {
      const result = await api(`/api/admin/reunions/${encodeURIComponent(reunion.id)}/editors`, {
        method: "PATCH",
        body: JSON.stringify({ ownerMemberId: Number(ownerMemberId), coAuthorMemberIds: [...coAuthorMemberIds] }),
      });
      onSaved(result.reunion);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal-panel reunion-editor-manager" role="dialog" aria-modal="true" aria-labelledby="reunion-editor-manager-title">
        <button className="icon-button modal-close" type="button" onClick={onClose} aria-label="Close reunion editor manager"><X aria-hidden="true" /></button>
        <p className="kicker">Reunion permissions</p>
        <h2 id="reunion-editor-manager-title">Choose the people who can edit.</h2>
        <p className="modal-intro">The owner and every co-author can update reunion text, photographs, crops, captions, and photograph comments. Assignment does not grant administrator access.</p>
        <form onSubmit={submit}>
          <label className="reunion-editor-search"><Search aria-hidden="true" /><span className="sr-only">Search classmates</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search names or branches…" /></label>
          {loading && <Loading label="Opening the class roster" />}
          {loadError && <ErrorMessage>{loadError}</ErrorMessage>}
          <div className="reunion-editor-list">
            {filtered.map((account) => {
              const name = `${account.firstName} ${account.lastName}`;
              const isOwner = Number(ownerMemberId) === account.memberId;
              return (
                <div className={isOwner ? "reunion-editor-row is-owner" : "reunion-editor-row"} key={account.memberId}>
                  <div><strong>{name}</strong><span>{account.branch} · {account.status.replaceAll("_", " ")}</span></div>
                  <label><input type="radio" name="reunion-owner" checked={isOwner} onChange={() => chooseOwner(account.memberId)} /> Owner</label>
                  <label><input type="checkbox" checked={coAuthorMemberIds.has(account.memberId)} disabled={isOwner} onChange={() => toggleCoAuthor(account.memberId)} /> Co-author</label>
                </div>
              );
            })}
          </div>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <div className="reunion-editor__actions">
            <button className="button button--ghost" type="button" onClick={onClose}>Cancel</button>
            <button className="button button--coral" type="submit" disabled={saving || !ownerMemberId}><Save aria-hidden="true" /> {saving ? "Saving…" : "Save editors"}</button>
          </div>
        </form>
      </section>
    </div>
  );
}

export default function ReunionDetail({ authenticated, canContribute, isAdministrator, canCurateCollegeDays, onLogin, onUpdated }) {
  const { reunionId } = useParams();
  const [searchParams] = useSearchParams();
  const requestedPhotoId = searchParams.get("photo") || "";
  const requestedAlbumId = searchParams.get("album") || "";
  const [selectedAlbum, setSelectedAlbum] = useState(0);
  const [showGate, setShowGate] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [movingPhotoId, setMovingPhotoId] = useState("");
  const [coveringPhotoId, setCoveringPhotoId] = useState("");
  const [reorderingPhotoId, setReorderingPhotoId] = useState("");
  const [deletingPhotoId, setDeletingPhotoId] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [moveError, setMoveError] = useState("");
  const [coverError, setCoverError] = useState("");
  const [moveNotice, setMoveNotice] = useState("");
  const [editingReunion, setEditingReunion] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [managingEditors, setManagingEditors] = useState(false);
  const loader = useCallback(
    () => authenticated ? api("/api/reunions") : Promise.resolve(null),
    [authenticated],
  );
  const { data, error, loading, refresh: reload, setData } = useApi(loader, [loader]);
  const reunion = data?.reunions?.find((item) => item.id === reunionId);
  const albums = useMemo(() => {
    if (!reunion) return [];
    const photoOrders = reunion.photoOrderByAlbum || {};
    const legacy = (reunion.albums || [])
      .map((id) => data?.albums?.find((album) => album.id === id))
      .filter(Boolean)
      .map((album) => ({ ...album, photos: orderAlbumPhotos(album.photos, photoOrders[album.id]) }));
    const sharedAlbumId = `${reunion.id}-shared`;
    const uploaded = reunion.photos?.length ? [{ id: sharedAlbumId, title: "Shared by classmates", description: "Photographs added by the class.", photos: orderAlbumPhotos(reunion.photos, photoOrders[sharedAlbumId]) }] : [];
    return [...legacy, ...uploaded];
  }, [data, reunion]);

  const linkedAlbumIndex = albums.findIndex((album) => album.id === requestedAlbumId);
  useEffect(() => {
    setSelectedAlbum(linkedAlbumIndex >= 0 ? linkedAlbumIndex : 0);
  }, [reunionId, linkedAlbumIndex, requestedPhotoId]);

  function replaceReunion(updatedReunion) {
    setData((current) => current ? {
      ...current,
      reunions: (current.reunions || []).map((item) => item.id === updatedReunion.id ? updatedReunion : item),
    } : current);
  }

  async function uploadPhotos(event) {
    const files = [...(event.target.files || [])];
    if (!files.length) return;
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    if (files.length > 10 || totalBytes > 25 * 1024 * 1024 || files.some((file) => file.size > 15 * 1024 * 1024)) {
      setUploadError("Choose up to 10 photographs totaling no more than 25 MB, with each image under 15 MB.");
      event.target.value = "";
      return;
    }
    setUploading(true);
    setUploadError("");
    const body = new FormData();
    files.forEach((file) => body.append("photos", file));
    try {
      await api(`/api/reunions/${reunion.id}/photos`, { method: "POST", body });
      onUpdated();
      await reload();
    } catch (requestError) {
      setUploadError(requestError.message);
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  }

  async function moveToCollegeDays(photo) {
    if (!activeAlbum || photo.collegeDaysDisabled || movingPhotoId || deletingPhotoId) return;
    const albumId = activeAlbum.id;
    setMovingPhotoId(photo.id);
    setMoveError("");
    setMoveNotice("");
    removePhotoFromView(albumId, photo.id);
    try {
      await api("/api/admin/college-days", {
        method: "POST",
        body: JSON.stringify({ albumId, photoId: photo.id, description: "" }),
      });
      setMoveNotice(`${photo.alt || "Photograph"} moved to College Days.`);
    } catch (requestError) {
      setMoveError(requestError.message);
      await reload();
    } finally {
      setMovingPhotoId("");
    }
  }

  async function makeCoverPhoto(photo) {
    if (!activeAlbum || !isAdministrator || movingPhotoId || deletingPhotoId || coveringPhotoId) return;
    setCoveringPhotoId(photo.id);
    setCoverError("");
    setMoveNotice("");
    try {
      const result = await api(`/api/admin/reunions/${encodeURIComponent(reunion.id)}/cover`, {
        method: "PATCH",
        body: JSON.stringify({ albumId: activeAlbum.id, photoId: photo.id }),
      });
      replaceReunion(result.reunion);
      onUpdated();
      setMoveNotice(`${photo.alt || "Photograph"} is now the reunion cover photo.`);
    } catch (requestError) {
      setCoverError(requestError.message);
    } finally {
      setCoveringPhotoId("");
    }
  }

  async function reorderPhoto(photo, targetPosition) {
    if (!activeAlbum || !reunion.canEdit || reorderingPhotoId) return;
    setReorderingPhotoId(photo.id);
    setMoveNotice("");
    try {
      const result = await api(`/api/reunions/${encodeURIComponent(reunion.id)}/photo-order`, {
        method: "PATCH",
        body: JSON.stringify({ albumId: activeAlbum.id, photoId: photo.id, targetPosition }),
      });
      replaceReunion(result.reunion);
      onUpdated();
      setMoveNotice(`${photo.alt || "Photograph"} moved to position ${targetPosition}.`);
    } finally {
      setReorderingPhotoId("");
    }
  }

  function removePhotoFromView(albumId, photoId) {
    setData((current) => current ? {
      ...current,
      albums: (current.albums || []).map((album) => album.id === albumId
        ? { ...album, photos: album.photos.filter((item) => item.id !== photoId) }
        : album),
      reunions: (current.reunions || []).map((item) => `${item.id}-shared` === albumId
        ? { ...item, photos: (item.photos || []).filter((candidate) => candidate.id !== photoId) }
        : item),
    } : current);
  }

  async function deletePhoto() {
    if (!deleteTarget || movingPhotoId || deletingPhotoId) return;
    const { albumId, photo } = deleteTarget;
    setDeletingPhotoId(photo.id);
    setDeleteError("");
    setMoveError("");
    setMoveNotice("");
    try {
      const result = isAdministrator
        ? await api(`/api/admin/reunion-albums/${encodeURIComponent(albumId)}/photos/${encodeURIComponent(photo.id)}`, { method: "DELETE" })
        : await api(`/api/reunions/${encodeURIComponent(reunion.id)}/photos/${encodeURIComponent(photo.id)}`, { method: "DELETE" });
      removePhotoFromView(albumId, photo.id);
      if (result?.reunion) replaceReunion(result.reunion);
      setMoveNotice(`${photo.alt || "Photograph"} deleted.`);
      setDeleteTarget(null);
    } catch (requestError) {
      setDeleteError(requestError.message);
    } finally {
      setDeletingPhotoId("");
    }
  }

  async function addPhotoComment(photo, body) {
    await api(`/api/reunions/${encodeURIComponent(reunion.id)}/photos/${encodeURIComponent(photo.id)}/comments`, {
      method: "POST",
      body: JSON.stringify({ body }),
    });
    await reload();
  }

  async function editPhotoComment(photo, commentId, body) {
    await api(`/api/reunions/${encodeURIComponent(reunion.id)}/photos/${encodeURIComponent(photo.id)}/comments/${commentId}`, {
      method: "PATCH",
      body: JSON.stringify({ body }),
    });
    await reload();
  }

  async function removePhotoComment(photo, comment) {
    await api(`/api/reunions/${encodeURIComponent(reunion.id)}/photos/${encodeURIComponent(photo.id)}/comments/${comment.id}`, {
      method: "DELETE",
    });
    await reload();
  }

  if (!authenticated) {
    return <div className="reunion-detail-gate section-wrap"><MemberGate onSuccess={onLogin} /></div>;
  }
  if (loading) return <div className="section-wrap"><Loading /></div>;
  if (error) return <div className="section-wrap"><ErrorMessage>{error}</ErrorMessage></div>;
  if (!reunion) return <div className="section-wrap"><ErrorMessage>That reunion could not be found.</ErrorMessage></div>;

  const activeAlbum = albums[selectedAlbum];
  const isMiniReunion = reunion.kind === "local";
  const sharedAlbumId = `${reunion.id}-shared`;
  const canManageSharedPhoto = Boolean(reunion.canEdit && activeAlbum?.id === sharedAlbumId);
  const canEditActivePhoto = Boolean(isAdministrator || canManageSharedPhoto);
  const canReorderActiveAlbum = Boolean(reunion.canEdit && activeAlbum);
  const photoCuration = isAdministrator || canManageSharedPhoto || canReorderActiveAlbum ? {
    movingPhotoId,
    move: canCurateCollegeDays ? moveToCollegeDays : null,
    coverPhotoSrc: reunion.cover,
    coveringPhotoId,
    makeCover: isAdministrator ? makeCoverPhoto : null,
    reorderingPhotoId,
    reorder: canReorderActiveAlbum ? reorderPhoto : null,
    editingPhotoId: editingPhoto?.photo?.id || "",
    edit: canEditActivePhoto ? (photo) => setEditingPhoto({ photo, albumId: activeAlbum.id }) : null,
    deletingPhotoId,
    delete: isAdministrator || canManageSharedPhoto ? (photo) => {
      setDeleteError("");
      setDeleteTarget({ photo, albumId: activeAlbum.id, albumTitle: activeAlbum.title });
    } : null,
    busy: Boolean(movingPhotoId || deletingPhotoId || coveringPhotoId || reorderingPhotoId),
  } : null;
  const photoComments = activeAlbum?.id === sharedAlbumId ? {
    canContribute,
    add: addPhotoComment,
    edit: editPhotoComment,
    remove: removePhotoComment,
  } : null;
  return (
    <>
      <section className="reunion-detail-hero">
        {reunion.cover && <img src={reunion.cover} alt="" style={cropStyle(reunion.coverCrop)} />}
        <div className="reunion-detail-hero__shade" />
        <div className="reunion-detail-hero__content section-wrap">
          <Link className="back-link" to="/reunions"><ArrowLeft aria-hidden="true" /> All reunions</Link>
          <p className="kicker">{reunion.eyebrow} · {reunion.year}</p>
          <h1>{reunion.title}</h1>
          {reunion.kind === "local" && (reunion.createdBy || reunion.owner) && (
            <div className="reunion-detail-hero__authors">
              <UserRound aria-hidden="true" />
              {reunion.createdBy && <span>Created by <strong>{reunion.createdBy.name}</strong></span>}
              {reunion.owner && (!reunion.createdBy || reunion.owner.memberId !== reunion.createdBy.memberId) && <span>Owned by <strong>{reunion.owner.name}</strong></span>}
              {(reunion.coAuthors?.length || 0) > 0 && <span>Co-authors <strong>{reunion.coAuthors.map((person) => person.name).join(", ")}</strong></span>}
            </div>
          )}
          <div className="reunion-detail-hero__facts">
            <span><CalendarDays aria-hidden="true" /> {reunion.date}{reunion.endDate ? ` — ${reunion.endDate}` : ""}</span>
            <span><MapPin aria-hidden="true" /> {reunion.location}</span>
          </div>
          <div className="reunion-detail-hero__actions">
            {reunion.canEdit && (
              <button className="button reunion-edit-button" type="button" onClick={() => setEditingReunion(true)}>
                <PencilLine aria-hidden="true" /> Edit reunion
              </button>
            )}
            {reunion.canManageEditors && (
              <button className="button reunion-editor-button" type="button" onClick={() => setManagingEditors(true)}>
                <UsersRound aria-hidden="true" /> Manage owner &amp; co-authors
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="reunion-story section-wrap">
        <div>
          <p className="kicker">The chapter</p>
          <h2>{reunion.description}</h2>
        </div>
        <aside>
          {reunion.organizer && <p><b>Organized by</b><br />{reunion.organizer}</p>}
          {reunion.contact && <p><b>Contact</b><br />{reunion.contact}</p>}
          {reunion.schedule?.length > 0 && <ul>{reunion.schedule.map((item) => <li key={item}>{item}</li>)}</ul>}
        </aside>
      </section>

      <section className={`album-section section-wrap${isMiniReunion ? " album-section--mini-reunion" : ""}`}>
        {!isMiniReunion && (
          <div className="album-section__heading">
            <div><p className="kicker">The photographs</p><h2>One frame at a time.</h2></div>
            {albums.length > 0 && (canContribute ? (
              <label className="button button--coral upload-button">
                <Upload aria-hidden="true" /> {uploading ? "Uploading…" : "Add photographs"}
                <input type="file" accept="image/jpeg,image/png,image/webp,image/heic" multiple disabled={uploading} onChange={uploadPhotos} />
              </label>
            ) : !authenticated ? (
              <button className="button button--ghost" type="button" onClick={() => setShowGate(true)}><Camera aria-hidden="true" /> Sign in to add photos</button>
            ) : null)}
          </div>
        )}
        {isMiniReunion && albums.length > 0 && canContribute && (
          <div className="mini-reunion-photo-actions">
            <label className="button button--coral upload-button">
              <Upload aria-hidden="true" /> {uploading ? "Uploading…" : "Add photographs"}
              <input type="file" accept="image/jpeg,image/png,image/webp,image/heic" multiple disabled={uploading} onChange={uploadPhotos} />
            </label>
          </div>
        )}
        {uploadError && <ErrorMessage>{uploadError}</ErrorMessage>}
        {moveError && <ErrorMessage>{moveError}</ErrorMessage>}
        {coverError && <ErrorMessage>{coverError}</ErrorMessage>}
        <p className="sr-only" role="status" aria-live="polite">{moveNotice}</p>
        {albums.length ? (
          <>
            {!isMiniReunion && (
              <>
                <div className="album-tabs" role="tablist" aria-label="Reunion albums">
                  {albums.map((album, index) => (
                    <button key={album.id} className={index === selectedAlbum ? "is-active" : ""} type="button" role="tab" aria-selected={index === selectedAlbum} onClick={() => setSelectedAlbum(index)}>
                      <span>{String(index + 1).padStart(2, "0")}</span>{album.title}<small>{album.locked ? "Members only" : `${album.photos.length} frames`}</small>
                    </button>
                  ))}
                </div>
                <div className="album-intro"><h3>{activeAlbum?.title}</h3><p>{activeAlbum?.description}</p></div>
              </>
            )}
            <PhotoStage
              album={activeAlbum}
              sharePath={`/reunions/${encodeURIComponent(reunionId)}`}
              requestedPhotoId={activeAlbum?.id === requestedAlbumId ? requestedPhotoId : ""}
              onUnlock={() => setShowGate(true)}
              curation={photoCuration}
              comments={photoComments}
            />
          </>
        ) : (
          <PhotoStage
            album={{ photos: [] }}
            emptyAction={canContribute ? (
              <label className="button button--ink upload-button">
                <Upload aria-hidden="true" /> {uploading ? "Uploading…" : "Add the first photographs"}
                <input type="file" accept="image/jpeg,image/png,image/webp,image/heic" multiple disabled={uploading} onChange={uploadPhotos} />
              </label>
            ) : !authenticated ? (
              <button className="button button--ink" type="button" onClick={() => setShowGate(true)}><Camera aria-hidden="true" /> Sign in to add photographs</button>
            ) : null}
          />
        )}
      </section>

      {showGate && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setShowGate(false)}>
          <div className="modal-panel modal-panel--gate" role="dialog" aria-modal="true"><MemberGate compact onSuccess={async () => { await onLogin(); await reload(); setShowGate(false); }} /></div>
        </div>
      )}
      {editingReunion && (
        <EditReunion
          reunion={reunion}
          onClose={() => setEditingReunion(false)}
          onSaved={(updatedReunion) => {
            replaceReunion(updatedReunion);
            onUpdated();
            setEditingReunion(false);
          }}
        />
      )}
      {editingPhoto && (
        <EditReunionPhoto
          reunion={reunion}
          albumId={editingPhoto.albumId}
          photo={editingPhoto.photo}
          onClose={() => setEditingPhoto(null)}
          onSaved={async (result) => {
            if (result.reunion) replaceReunion(result.reunion);
            else await reload();
            onUpdated();
            setEditingPhoto(null);
            requestAnimationFrame(() => document.querySelector(".photo-stage__edit")?.focus({ preventScroll: true }));
          }}
        />
      )}
      {managingEditors && (
        <ManageReunionEditors
          reunion={reunion}
          onClose={() => setManagingEditors(false)}
          onSaved={(updatedReunion) => {
            replaceReunion(updatedReunion);
            onUpdated();
            setManagingEditors(false);
          }}
        />
      )}
      {deleteTarget && (
        <DeleteConfirmation
          kicker={isAdministrator ? "Administrator action" : "Reunion editor action"}
          title={`Delete this photograph from “${deleteTarget.albumTitle}”?`}
          description="This removes the photograph from the album. Uploaded files and related comments are permanently deleted. This action cannot be undone."
          confirmLabel="Delete photograph"
          busy={Boolean(deletingPhotoId)}
          error={deleteError}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={deletePhoto}
        />
      )}
    </>
  );
}
